import React from "react";
import styled from "styled-components";
import DeltaBadge from "../../DeltaBadge";
import { COLORS } from "../../../utils/colors";
import { formatKvalue } from "../../../utils/numberUtils";

export interface StoreData {
  storeName: string;
  caN: number;
  caN1: number;
  clientsN: number;
  progClients: number;
  progPrev: number;
  quotePart: number;
}

interface StoreMetricsCardProps {
  store: StoreData;
}

const formatPercent = (val: number, showSign = true): string => {
  const formatted = new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(val);
  return showSign && val > 0 ? `+${formatted}\u00A0%` : `${formatted}\u00A0%`;
};

export const StoreMetricsCard: React.FC<StoreMetricsCardProps> = ({ store }) => {
  const revenueGrowth = store.caN1 !== 0 ? ((store.caN - store.caN1) / store.caN1) * 100 : 0;

  return (
    <CardContainer>
      <Header>
        <StoreNameWrapper>
          <IndicatorDot />
          <StoreName>{store.storeName}</StoreName>
        </StoreNameWrapper>
        <DeltaBadge value={revenueGrowth} />
      </Header>

      <GridContainer>
        <GridItem>
          <Label>CA N</Label>
          <Value>{formatKvalue(store.caN)}</Value>
        </GridItem>
        <GridItem>
          <Label>CA N-1</Label>
          <Value>{formatKvalue(store.caN1)}</Value>
        </GridItem>
        <GridItem>
          <Label>Clients N</Label>
          <Value>{new Intl.NumberFormat("fr-FR").format(store.clientsN)}</Value>
        </GridItem>
        <GridItem>
          <Label>Prog Clients</Label>
          <PercentValue $isPositive={store.progClients >= 0}>
            {formatPercent(store.progClients)}
          </PercentValue>
        </GridItem>
      </GridContainer>

      <Footer>
        <FooterLeft>
          Ecart : <ColoredText $isPositive={store.progPrev >= 0}>
            {formatKvalue(store.progPrev)}
          </ColoredText>
        </FooterLeft>
        <FooterRight>
          Quote part : <BoldBlackText>{'-'}</BoldBlackText>
        </FooterRight>
      </Footer>
    </CardContainer>
  );
};

export default StoreMetricsCard;

// Styled Components
const CardContainer = styled.div`
  /* 8px indicator dot + 8px gap = 16px offset to align perfectly with StoreName */
  --store-name-indent: 16px;

  background: #ffffff;
  border: 1px solid #ebebeb;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  text-align: left;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StoreNameWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const IndicatorDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${COLORS?.green || "#00875A"};
  flex-shrink: 0;
`;

const StoreName = styled.span`
  font-family: Ubuntu, sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #121212;
  text-align: left;
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  padding-left: var(--store-name-indent);
  justify-items: start; /* Forces grid items to start on the left edge */
`;

const GridItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start; /* Ensures label and value align left internally */
  gap: 4px;
  text-align: left;
`;

const Label = styled.span`
  font-size: 12px;
  color: #696969;
  font-weight: 500;
  text-align: left;
`;

const Value = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: #121212;
  text-align: left;
`;

const PercentValue = styled.span<{ $isPositive: boolean }>`
  font-size: 14px;
  font-weight: 700;
  color: ${({ $isPositive }) => ($isPositive ? COLORS?.green || "#00875A" : COLORS?.red || "#DE350B")};
  text-align: left;
`;

const Footer = styled.div`
  border-top: 1px solid #ebebeb;
  padding-top: 12px;
  padding-left: var(--store-name-indent);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FooterLeft = styled.span`
  font-size: 13px;
  color: #696969;
  text-align: left;
`;

const ColoredText = styled.span<{ $isPositive: boolean }>`
  color: ${({ $isPositive }) => ($isPositive ? COLORS?.green || "#00875A" : COLORS?.red || "#DE350B")};
  font-weight: 700;
`;

const FooterRight = styled.span`
  font-size: 13px;
  color: #696969;
`;

const BoldBlackText = styled.span`
  color: #121212;
  font-weight: 700;
`;